/**
 * <MonthCalendar> — un mois, ses jours en grille, et le jour qu'on touche (molecule).
 *
 * Le calendrier du design system : celui que `<DatePicker>` ouvre dans sa feuille, à la place du
 * calendrier Material de `react-native-paper-dates` (en-tête bleu de l'ancien thème, ronds blancs —
 * Wajih, 03/11). Le mois en tête avec ses deux flèches, les initiales des jours (la semaine part du
 * lundi), puis les jours :
 *  - choisi — l'aplat choisi de l'univers (`role-selected`) et son encre, comme une pilule cochée ;
 *  - aujourd'hui — cerné de la couleur de l'univers ;
 *  - hors des bornes — à l'encre secondaire, inerte (les flèches s'arrêtent aux mois des bornes).
 *
 * Contrôlé pour le jour (`value`, `onSelect`) ; le mois montré est un état d'interface : il part du
 * mois de `openOn` (ou de la valeur, ou d'aujourd'hui) et vit dans le composant.
 */
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { FocusRing, focusRingHost } from "../../atoms/FocusRing"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { inkOnFill } from "../../theme/tokens/colors"
import { formatLongDate, formatMonthYear, toDateOnly } from "../../../ports/primary/ui/formatters/dateFormatter"
import { inRange, monthOf, monthWeeks, shiftMonth } from "./monthGrid"

/** `<Pressable>` ne type que `pressed` ; react-native-web ajoute `focused` et `hovered` à l'exécution. */
type PressableVisualState = { pressed: boolean; focused?: boolean; hovered?: boolean }

export type MonthCalendarProps = {
    /** Le jour choisi, `YYYY-MM-DD` ; vide : aucun. */
    value: string
    onSelect: (day: string) => void
    /** Le jour (`YYYY-MM-DD`) dont le mois s'ouvre. Défaut : la valeur, sinon aujourd'hui. */
    openOn?: string
    /** Premier et dernier jours choisissables, `YYYY-MM-DD`, bornes comprises. Absents : sans borne. */
    minDay?: string | null
    maxDay?: string | null
    testID?: string
}

const WEEKDAYS = ["L", "M", "M", "J", "V", "S", "D"]
const WEEKDAY_NAMES = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"]

/** La case d'un jour : 44 px de cible, le rond posé dedans. */
const DAY = 44
const DOT = 38

const TEXTS = { previous: "Mois précédent", next: "Mois suivant" }

const CHOSEN = "role-selected"

export function MonthCalendar(props: MonthCalendarProps) {
    const { value, onSelect, openOn, minDay = null, maxDay = null, testID } = props
    const today = toDateOnly(new Date())
    const [month, setMonth] = useState(() => monthOf(openOn || value || today))

    const canGoBack = minDay == null || shiftMonth(month, -1) >= monthOf(minDay)
    const canGoForward = maxDay == null || shiftMonth(month, 1) <= monthOf(maxDay)

    return (
        <Stack gap="2" style={styles.grid} testID={testID}>
            <Row justify="between" align="center">
                <IconButton
                    iconName="chevron-left"
                    appearance="transparent"
                    iconColor="text-primary"
                    size={DAY}
                    iconSize={20}
                    disabled={!canGoBack}
                    onPress={() => setMonth((m) => shiftMonth(m, -1))}
                    accessibilityLabel={TEXTS.previous}
                />
                <Text size="text-base" weight="bold" color="text-strong" accessibilityRole="header">
                    {formatMonthYear(month)}
                </Text>
                <IconButton
                    iconName="chevron-right"
                    appearance="transparent"
                    iconColor="text-primary"
                    size={DAY}
                    iconSize={20}
                    disabled={!canGoForward}
                    onPress={() => setMonth((m) => shiftMonth(m, 1))}
                    accessibilityLabel={TEXTS.next}
                />
            </Row>
            <View style={styles.week}>
                {WEEKDAYS.map((letter, index) => (
                    <View key={WEEKDAY_NAMES[index]} style={styles.cell} accessibilityLabel={WEEKDAY_NAMES[index]}>
                        <Text size="text-sm" weight="semibold" color="text-muted">
                            {letter}
                        </Text>
                    </View>
                ))}
            </View>
            {monthWeeks(month).map((week) => (
                <View key={week.find(Boolean) ?? ""} style={styles.week}>
                    {week.map((day, index) =>
                        day == null ? (
                            <View key={`empty-${index}`} style={styles.cell} />
                        ) : (
                            <CalendarDay
                                key={day}
                                day={day}
                                selected={day === value.slice(0, 10)}
                                today={day === today}
                                enabled={inRange(day, minDay, maxDay)}
                                onPress={() => onSelect(day)}
                            />
                        )
                    )}
                </View>
            ))}
        </Stack>
    )
}

function CalendarDay(props: { day: string; selected: boolean; today: boolean; enabled: boolean; onPress: () => void }) {
    const { day, selected, today, enabled, onPress } = props
    return (
        <Pressable
            onPress={onPress}
            disabled={!enabled}
            accessibilityRole="button"
            accessibilityLabel={formatLongDate(day)}
            accessibilityState={{ selected, disabled: !enabled }}
            style={[focusRingHost, styles.cell]}
        >
            {({ pressed, focused, hovered }: PressableVisualState) => (
                <View style={styles.dot(selected, today, enabled && (pressed || hovered === true))}>
                    <FocusRing visible={focused === true} radius={DOT / 2} />
                    <Text
                        size="text-md"
                        weight={selected || today ? "bold" : "medium"}
                        color={selected ? inkOnFill(CHOSEN) : enabled ? "text-primary" : "text-muted"}
                    >
                        {String(Number(day.slice(8, 10)))}
                    </Text>
                </View>
            )}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Sept cases et leurs écarts, centrées là où on pose le calendrier. */
    grid: { alignSelf: "center", width: 7 * DAY + 6 * theme.spacing["1"], maxWidth: "100%" },
    week: { flexDirection: "row", justifyContent: "space-between" },
    cell: {
        width: DAY,
        height: DAY,
        alignItems: "center",
        justifyContent: "center",
    },
    dot: (selected: boolean, today: boolean, pressed: boolean) => ({
        width: DOT,
        height: DOT,
        borderRadius: theme.radius.full,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: selected ? theme.colors[CHOSEN] : pressed ? theme.colors["surface-subtle"] : "transparent",
        borderWidth: today && !selected ? 2 : 0,
        borderColor: theme.colors.role,
    }),
}))
